import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, Package } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Account() {
  const { user, login, register, loginWithGoogle, logout } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setOrdersLoading(true);
      api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])).finally(() => setOrdersLoading(false));
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back.");
      } else {
        await register(form.name, form.email, form.password);
        toast.success("Your LIRA account is ready.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (user) {
    return (
      <div className="container-lira py-20 min-h-[70vh]" data-testid="account-dashboard">
        <div className="max-w-xl mx-auto text-center">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="h-20 w-20 rounded-full object-cover mx-auto" />
          ) : (
            <div className="h-20 w-20 rounded-full bg-beige grid place-items-center mx-auto font-serif text-3xl">{(user.name || user.email)[0].toUpperCase()}</div>
          )}
          <h1 className="font-serif text-4xl md:text-5xl font-light mt-6">Hello, {user.name || "there"}</h1>
          <p className="text-[#737373] mt-2 flex items-center justify-center gap-2"><Mail strokeWidth={1} className="h-4 w-4" /> {user.email}</p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button onClick={() => navigate("/collection/all")} className="btn-secondary">Continue Shopping</button>
            <button onClick={() => navigate("/wishlist")} className="btn-secondary">View Wishlist</button>
          </div>
          {user.is_admin && (
            <button onClick={() => navigate("/admin")} className="btn-primary mt-4 w-full" data-testid="admin-link">Manage Orders (Admin)</button>
          )}
          <button onClick={logout} className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#737373] hover:text-[#1A1A1A]" data-testid="logout-button">
            <LogOut strokeWidth={1} className="h-4 w-4" /> Sign Out
          </button>
        </div>

        <div className="max-w-3xl mx-auto mt-20" data-testid="my-orders">
          <div className="flex items-center gap-3 mb-8">
            <Package strokeWidth={1} className="h-5 w-5" />
            <h2 className="font-serif text-3xl font-light">My Orders</h2>
          </div>
          {ordersLoading ? (
            <p className="text-[#737373] text-sm">Loading your orders…</p>
          ) : orders.length === 0 ? (
            <div className="border border-[#E5E5E5] rounded-sm p-10 text-center">
              <p className="text-[#737373]">You haven't placed any orders yet.</p>
              <button onClick={() => navigate("/collection/all")} className="btn-secondary mt-6">Start Shopping</button>
            </div>
          ) : (
            <div className="space-y-5">
              {orders.map((o) => (
                <div key={o.order_id} className="border border-[#E5E5E5] rounded-sm p-6" data-testid={`order-${o.order_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-[#E5E5E5]">
                    <div>
                      <p className="text-xs uppercase tracking-[0.15em] text-[#737373]">Order</p>
                      <p className="text-sm font-medium">#{o.order_id}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[#737373]">{o.captured_at ? new Date(o.captured_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : ""}</p>
                      <span className="inline-block mt-1 text-[10px] uppercase tracking-[0.15em] bg-[#1A1A1A] text-white px-3 py-1 rounded-full capitalize">{o.fulfillment_status || "processing"}</span>
                    </div>
                  </div>
                  <div className="py-4 divide-y divide-[#F0EEE9]">
                    {(o.line_items || []).map((li, idx) => (
                      <div key={idx} className="flex items-center gap-4 py-3">
                        {li.image && <img src={li.image} alt={li.name} className="h-16 w-12 object-cover rounded-sm bg-beige" />}
                        <div className="flex-1">
                          <p className="font-serif text-lg leading-tight">{li.name}</p>
                          <p className="text-xs text-[#737373]">{li.color ? `${li.color} · ` : ""}Qty {li.quantity}</p>
                        </div>
                        <span className="text-sm">${(li.price * li.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-[#E5E5E5] text-sm">
                    <span className="text-[#737373]">Total {o.shipping ? "(incl. shipping)" : ""}</span>
                    <span className="text-base">${(o.amount || 0).toFixed(2)}</span>
                  </div>
                  {o.tracking_number && (
                    <p className="text-xs text-[#737373] mt-3">Tracking: {o.carrier ? `${o.carrier} · ` : ""}<span className="text-[#1A1A1A]">{o.tracking_number}</span></p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container-lira py-20 min-h-[70vh]" data-testid="account-auth">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-10">
          <h1 className="font-serif text-4xl md:text-5xl font-light">{mode === "login" ? "Welcome Back" : "Create Account"}</h1>
          <p className="text-[#737373] mt-3">{mode === "login" ? "Sign in to your LIRA account." : "Join the LIRA community."}</p>
        </div>

        <button onClick={loginWithGoogle} className="btn-secondary w-full mb-6" data-testid="google-login-button">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4 mr-3" /> Continue with Google
        </button>

        <div className="flex items-center gap-4 my-6">
          <div className="h-px flex-1 bg-[#E5E5E5]" /><span className="eyebrow">or</span><div className="h-px flex-1 bg-[#E5E5E5]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" required className="w-full border border-[#E5E5E5] rounded-full px-6 py-3.5 text-sm outline-none focus:ring-1 focus:ring-black" data-testid="name-input" />
          )}
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" required className="w-full border border-[#E5E5E5] rounded-full px-6 py-3.5 text-sm outline-none focus:ring-1 focus:ring-black" data-testid="email-input" />
          <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" required minLength={6} className="w-full border border-[#E5E5E5] rounded-full px-6 py-3.5 text-sm outline-none focus:ring-1 focus:ring-black" data-testid="password-input" />
          <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="auth-submit">
            {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <p className="text-center text-sm text-[#737373] mt-6">
          {mode === "login" ? "New to LIRA?" : "Already have an account?"}{" "}
          <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-[#1A1A1A] underline" data-testid="toggle-auth-mode">
            {mode === "login" ? "Create an account" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
}
